const isEqual = require('lodash/isEqual')
const { importProductData } = require('./import')
const { getConfigurationsByIds } = require('../../../configuration/api/read/get-by-ids')
const { codeToProductId } = require('../../tools/import-validation/content/utils')

const resolveRowId = row => (row._id ? String(row._id).toLowerCase() : codeToProductId(row.code))

const serviceEntryFromRow = (row, facilityKey, serviceId) => {
  const entry = { id: serviceId }
  const productLine = row[facilityKey.replace('.hasService', '.productLine')]
  const index = row[facilityKey.replace('.hasService', '.index')]
  if (productLine) entry.productLine = productLine
  if (index) entry.index = index
  return entry
}

const addTo = (map, key, productId) => {
  const existing = map.get(key) || new Set()
  existing.add(productId)
  map.set(key, existing)
}

const sortedById = services => [...services].sort((a, b) => a.id.localeCompare(b.id))

// A facility service whose column IS in the file gets fully
// replaced by this row - 'YES' sets it, blank removes it. A service
// whose column is NOT in the file is not affected.
const computeFacilityServices = (doc, row, context) => {
  const existingById = new Map((doc.services || []).map(service => [service.id, service]))
  const services = []

  for (const { facilityKeys } of context.serviceColumnsByProgram.values()) {
    for (const key of facilityKeys) {
      const serviceId = context.columnKeyToServiceId.get(key)
      if (!serviceId) continue

      if (!context.columnKeysInSheet.has(key)) {
        const existing = existingById.get(serviceId)
        if (existing) services.push(existing)
        continue
      }

      if (row[key] === 'YES') {
        services.push(serviceEntryFromRow(row, key, serviceId))
      }
    }
  }

  return services
}

// Tracks, across every row, which products should end up added to / removed
// from each service's configuration doc products[] - facility and warehouse.
const trackConfigurationMembership = (row, productId, context, toAdd, toRemove) => {
  for (const { facilityKeys, warehouseKey } of context.serviceColumnsByProgram.values()) {
    for (const key of facilityKeys) {
      if (!context.columnKeysInSheet.has(key)) continue
      const serviceId = context.columnKeyToServiceId.get(key)
      if (!serviceId) continue
      if (row[key] === 'YES') addTo(toAdd, serviceId, productId)
      else addTo(toRemove, serviceId, productId)
    }

    if (!warehouseKey || !context.columnKeysInSheet.has(warehouseKey)) continue
    const warehouseServiceId = context.columnKeyToServiceId.get(warehouseKey)
    if (!warehouseServiceId) continue
    if (row[warehouseKey] === 'YES') addTo(toAdd, warehouseServiceId, productId)
    else addTo(toRemove, warehouseServiceId, productId)
  }
}

async function commitProducts (state, rows, context) {
  if (!rows.length) {
    return { created: 0, updated: 0, configurationsUpdated: 0 }
  }

  const rowsForImport = rows.map(row => ({ ...row }))
  const dryRunResult = await importProductData(state, rowsForImport, { dryRun: true })
  const rowsById = new Map(rows.map(row => [resolveRowId(row), row]))

  const newDocs = dryRunResult.new
  const changedDocs = dryRunResult.update
  const unchangedCandidates = dryRunResult.unchangedIds
    .map(id => context.allTargetDBProducts && context.allTargetDBProducts[id])
    .filter(Boolean)
    .map(doc => ({ ...doc }))

  const toAdd = new Map()
  const toRemove = new Map()

  for (const doc of [...newDocs, ...changedDocs, ...unchangedCandidates]) {
    const row = rowsById.get(doc._id)
    trackConfigurationMembership(row, doc._id, context, toAdd, toRemove)
  }

  const otherProductDocs = []

  for (const doc of changedDocs) {
    const row = rowsById.get(doc._id)
    doc.services = computeFacilityServices(doc, row, context)
    otherProductDocs.push(doc)
  }

  for (const doc of unchangedCandidates) {
    const row = rowsById.get(doc._id)
    const services = computeFacilityServices(doc, row, context)
    if (isEqual(sortedById(services), sortedById(doc.services || []))) continue
    doc.services = services
    doc.updatedAt = new Date().toISOString()
    doc.updatedBy = state.user ? state.user.name : ''
    otherProductDocs.push(doc)
  }

  for (const doc of newDocs) {
    const row = rowsById.get(doc._id)
    doc.services = computeFacilityServices(doc, row, context)
  }

  const serviceIds = [...new Set([...toAdd.keys(), ...toRemove.keys()])]
  const configDocs = serviceIds.length
    ? await getConfigurationsByIds(state, serviceIds.map(id => `configuration:${id}`))
    : []

  if (configDocs.length !== serviceIds.length) {
    const foundServiceIds = new Set(configDocs.map(doc => doc.service))
    const missing = serviceIds.filter(id => !foundServiceIds.has(id))
    throw new Error(`Missing configuration doc(s) for service(s): ${missing.join(', ')} - cannot commit`)
  }

  const originalConfigDocsById = new Map(configDocs.map(doc => [doc._id, doc]))
  const username = state.user ? state.user.name : ''
  const updatedConfigDocs = []

  for (const doc of configDocs) {
    const current = new Set(doc.products || [])
    for (const id of (toAdd.get(doc.service) || [])) current.add(id)
    for (const id of (toRemove.get(doc.service) || [])) current.delete(id)
    const products = [...current]

    if (isEqual([...products].sort(), [...(doc.products || [])].sort())) continue

    updatedConfigDocs.push({
      ...doc,
      products,
      updatedAt: new Date().toISOString(),
      updatedBy: username
    })
  }

  const combinedDocs = [...newDocs, ...otherProductDocs, ...updatedConfigDocs]

  if (!combinedDocs.length) {
    return { created: 0, updated: 0, configurationsUpdated: 0 }
  }

  const response = await state.productsDB.bulkDocs(combinedDocs)
  const failures = response.filter(row => row.error)

  if (!failures.length) {
    return {
      created: newDocs.length,
      updated: otherProductDocs.length,
      configurationsUpdated: updatedConfigDocs.length
    }
  }

  // Rollback - if there is any error in the saving process, rollback the
  // entire save, do not save partially.
  const newDocsEnd = newDocs.length
  const otherProductDocsEnd = newDocsEnd + otherProductDocs.length

  const compensatingDocs = []
  response.forEach((result, index) => {
    if (result.error) return
    const original = combinedDocs[index]

    if (index < newDocsEnd) {
      compensatingDocs.push({ _id: original._id, _rev: result.rev, _deleted: true })
    } else if (index < otherProductDocsEnd) {
      const before = context.allTargetDBProducts[original._id]
      compensatingDocs.push({ ...before, _rev: result.rev })
    } else {
      const before = originalConfigDocsById.get(original._id)
      compensatingDocs.push({ ...before, _rev: result.rev })
    }
  })

  if (compensatingDocs.length) {
    try {
      await state.productsDB.bulkDocs(compensatingDocs)
    } catch (rollbackErr) {
      console.error('CRITICAL: rollback after a partial commit failure itself failed - manual intervention needed', rollbackErr, compensatingDocs)
    }
  }

  const err = new Error('Commit failed - nothing was persisted')
  err.data = failures
  throw err
}

module.exports = { commitProducts }
